import { useState } from 'react';
import { format } from 'date-fns';
import { Eye, ChevronDown, Filter, FileSpreadsheet, FileJson, RotateCcw } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { usePayrollRuns } from '@/hooks/payroll/usePayrollRuns';
import { usePayrollRollback } from '@/hooks/payroll/usePayrollRollback';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { useAuth } from '@/hooks/useAuth';
import { PayrollRollbackDialog } from './PayrollRollbackDialog';
import type { PayrollRunWithDetails } from '@/services/payroll/payrollHistoryService';

interface PayrollRunsTableProps {
  onViewDetails: (run: PayrollRunWithDetails) => void;
  onExport?: (runId: string, format: 'csv' | 'json') => void;
}

const statusConfig = {
  draft: { label: 'Draft', variant: 'secondary' as const },
  processing: { label: 'Processing', variant: 'default' as const },
  completed: { label: 'Completed', variant: 'default' as const },
  completed_with_errors: { label: 'Completed with errors', variant: 'secondary' as const },
  failed: { label: 'Failed', variant: 'destructive' as const },
  rolled_back: { label: 'Rolled Back', variant: 'outline' as const },
};

/** Runs that produced payslips — exportable and rollback-able. */
const isFinishedRun = (status: string) =>
  status === 'completed' || status === 'completed_with_errors';

export function PayrollRunsTable({ 
  onViewDetails, 
  onExport 
}: PayrollRunsTableProps) {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [rollbackRun, setRollbackRun] = useState<PayrollRunWithDetails | null>(null);
  
  const { profile } = useAuth();
  const rollbackMutation = usePayrollRollback();
  
  const { data: runs, isLoading } = usePayrollRuns(
    statusFilter !== 'all' ? { status: statusFilter } : undefined
  );
  const { currencyCode } = useWorkplaceCurrency();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const filteredRuns = runs?.filter(run => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      run.run_number.toLowerCase().includes(query) ||
      run.creator_name?.toLowerCase().includes(query)
    );
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by run number or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="completed_with_errors">Completed with errors</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="rolled_back">Rolled Back</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Run Number</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Employees</TableHead>
              <TableHead>Total Gross</TableHead>
              <TableHead>Total Net</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!filteredRuns || filteredRuns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No payroll runs found
                </TableCell>
              </TableRow>
            ) : (
              filteredRuns.map((run) => {
                const status = statusConfig[run.status as keyof typeof statusConfig];
                return (
                  <TableRow key={run.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="font-medium">{run.run_number}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {format(new Date(run.start_date), 'MMM d, yyyy')} -{' '}
                        {format(new Date(run.end_date), 'MMM d, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={status?.variant ?? 'outline'}>
                        {status?.label ?? run.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{run.payslips_generated || 0}</TableCell>
                    <TableCell>{formatCurrency(Number(run.total_gross_pay || 0))}</TableCell>
                    <TableCell>{formatCurrency(Number(run.total_net_pay || 0))}</TableCell>
                    <TableCell>{run.creator_name || 'System'}</TableCell>
                    <TableCell>
                      {format(new Date(run.created_at), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            Actions
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onViewDetails(run)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          {onExport && isFinishedRun(run.status) && (
                            <>
                              <DropdownMenuItem onClick={() => onExport(run.id, 'csv')}>
                                <FileSpreadsheet className="mr-2 h-4 w-4" />
                                Export CSV
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => onExport(run.id, 'json')}>
                                <FileJson className="mr-2 h-4 w-4" />
                                Export JSON
                              </DropdownMenuItem>
                            </>
                          )}
                          {isFinishedRun(run.status) && (
                            <DropdownMenuItem
                              onClick={() => setRollbackRun(run)}
                              className="text-destructive focus:text-destructive"
                            >
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Rollback
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <PayrollRollbackDialog
        run={rollbackRun}
        open={!!rollbackRun}
        onOpenChange={(open) => { if (!open) setRollbackRun(null); }}
        onConfirm={(runId, reason) => {
          if (!profile?.id) return;
          rollbackMutation.mutate(
            { runId, reason, performedBy: profile.id },
            { onSuccess: (result) => { if (result.success) setRollbackRun(null); } }
          );
        }}
        isRollingBack={rollbackMutation.isPending}
      />
    </div>
  );
}
